import { SourceType, type NavItem, type NavigationConfig } from "./types";

type LayoutSourceType =
  | "route"
  | "internal"
  | "url"
  | "action"
  | "cdn"
  | string;

interface LayoutSource {
  type?: LayoutSourceType;
  key?: string;
  url?: string;
}

interface LayoutVisibility {
  value?: unknown;
  isVisible?: boolean;
  enable?: {
    isEnable?: boolean;
  };
}

interface LayoutNavItem {
  id?: string;
  type?: string;
  label?: string;
  source?: LayoutSource;
  visible?: LayoutVisibility;
  children?: LayoutNavItem[];
}

const mapVisibility = (visible?: LayoutVisibility) =>
  visible ? { isVisible: visible.isVisible } : undefined;

export interface LayoutNavigationResponse {
  data?: {
    ruleData?: {
      uiElements?: {
        uiLayout?: {
          theme?: {
            color?: {
              bg?: {
                base?: string;
              };
              text?: {
                primary?: string;
                active?: string;
                secondary?: string;
              };
              border?: {
                default?: string;
              };
            };
          };
          navigation?: {
            topNav?: {
              logo?: {
                type?: string;
                source?: LayoutSource;
                alt?: string;
              };
              children?: LayoutNavItem[];
              profileMenu?: {
                avatar?: {
                  showInitials?: boolean;
                };
                userInfo?: {
                  showName?: boolean;
                  showEmail?: boolean;
                };
                children?: LayoutNavItem[];
              };
            };
          };
        };
      };
    };
  };
}

const normalizeSource = (
  source: LayoutSource | undefined,
  fallbackKey: string
) => {
  const value = source?.url ?? "";
  if (source?.type === "action") {
    return {
      type: "action" as const,
      key: source.key ?? fallbackKey,
      value: value || source.key || fallbackKey
    };
  }

  const isExternal =
    value.startsWith("http://") || value.startsWith("https://");
  if (isExternal || source?.type === "url") {
    return {
      type: "url" as const,
      key: source?.key ?? fallbackKey,
      value
    };
  }

  return {
    type: "route" as const,
    key: source?.key ?? fallbackKey,
    value
  };
};

// The "My Queue" nav item's children each represent a program type (id is
// the program type code, e.g. "LAP", "BL"). Selecting one persists that
// program type (see NavbarContext.navigateFromSource) alongside whatever
// route the child's own source points to.
const mapQueueProgramTypeChild = (
  child: LayoutNavItem,
  childIndex: number
): NavItem => {
  const programType = child?.id;
  const mapped = mapLayoutItem(child, childIndex);

  if (!programType) return mapped;

  // These children often arrive with no "source" of their own (they're
  // buttons meant only to persist a program type) — fall back to a
  // no-op source so navigateFromSource still runs setProgramType.
  return {
    ...mapped,
    source: {
      type: mapped.source?.type ?? SourceType.None,
      key: mapped.source?.key ?? programType,
      value: mapped.source?.value ?? "",
      ...mapped.source,
      programType
    }
  };
};

const mapLayoutItem = (item: LayoutNavItem, index: number): NavItem => {
  const mappedType = item.type === "button" ? "action" : item.type;
  const fallbackId = `${mappedType || "link"}_${index + 1}`;
  const isQueueItem = item.id === "queue";

  return {
    id: item.id || fallbackId,
    type:
      mappedType === "dropdown" || mappedType === "group"
        ? mappedType
        : mappedType === "logo"
          ? "logo"
          : mappedType === "action"
            ? "action"
            : "link",
    label: item.label,
    visible: mapVisibility(item.visible),
    source: item.source
      ? normalizeSource(item.source, item.id || fallbackId)
      : undefined,
    children:
      item.children?.map((child, childIndex) =>
        isQueueItem
          ? mapQueueProgramTypeChild(child, childIndex)
          : mapLayoutItem(child, childIndex)
      ) ?? undefined
  };
};

export const mapUiLayoutToNavigationConfig = (
  response: LayoutNavigationResponse
): NavigationConfig | null => {
  const uiLayout = response?.data?.ruleData?.uiElements?.uiLayout;
  const topNav = uiLayout?.navigation?.topNav;
  const theme = uiLayout?.theme?.color;

  if (!topNav || !theme) return null;
  if (!theme?.bg?.base || !theme?.text?.primary || !theme?.text?.active) {
    return null;
  }

  const logoItem = topNav.logo
    ? {
        id: "logo",
        type: "logo" as const,
        position: 1,
        label: "FinBox",
        branding: {
          clientName: "FinBox",
          logo: topNav.logo.source?.url,
          altText: topNav.logo.alt ?? "FinBox Logo"
        }
      }
    : null;

  const childItems =
    topNav.children?.map((item, index) => ({
      ...mapLayoutItem(item, index),
      position: index + 2
    })) ?? [];

  return {
    themeConfig: {
      backgroundColor: theme.bg.base,
      textColor: theme.text.primary,
      activeTextColor: theme.text.active,
      hoverTextColor: theme.text.secondary || theme.text.primary,
      borderColor: theme.border?.default || theme.text.primary
    },
    topNav: [logoItem, ...childItems].filter(
      Boolean
    ) as NavigationConfig["topNav"],
    profileMenu: topNav.profileMenu
      ? {
          id: "profile-menu",
          type: "profileMenu",
          avatar: {
            showInitials: topNav.profileMenu.avatar?.showInitials !== false
          },
          userInfo: {
            showName: topNav.profileMenu.userInfo?.showName !== false,
            showEmail: topNav.profileMenu.userInfo?.showEmail !== false
          },
          items:
            topNav.profileMenu.children?.map((item, index) =>
              mapLayoutItem(item, index)
            ) ?? []
        }
      : undefined
  };
};
